import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";

import apiRoutes from "../../../../constants/api-routes";
import messageLevels from "../../../../constants/message-levels";
import messages from "../../../../constants/messages";
import navigationItemReferences from "../../../../constants/navigation-item-references";
import pages from "../../../../constants/pages";
import { getDataGridFakeData } from "../../../../helpers/coappDataGridUtils";
import useAuthHeader from "../../../../hooks/useAuthHeader";
import useCoAppNavigation from "../../../../hooks/useCoAppNavigation";
import useDatagridSorting from "../../../../hooks/useDatagridSorting";
import useToastAlert from "../../../../hooks/useToastAlert";
import { selectOrganizationIdentityProvider, selectUser } from "../../../../redux/userSlice";
import CoAppDatagridHeader from "../../../global/components/datagrid/CoAppDatagridHeader";
import CoAppStandardDataGrid from "../../../global/components/datagrid/CoAppStandardDataGrid";
import CoAppAddItemsToListModal from "../../../global/components/modals/CoAppAddItemsToListModal";
import CoAppDestructiveConfirmationModal from "../../../global/components/modals/CoAppDestructiveConfirmationModal";
import CoAppEditNameModal from "../../../global/components/modals/CoAppEditNameModal";

import { getRoleSSOColumns, roleIdpGroupColumns } from "./columns/role-sso-columns";

export default function RoleSSO() {
	const dummyRows = getDataGridFakeData(roleIdpGroupColumns);
	const authHeader = useAuthHeader();
	const identityProviderName = useSelector(selectOrganizationIdentityProvider);
	const currentUser = useSelector(selectUser);
	const { updateCoAppNavigation } = useCoAppNavigation();
	const { sortModel, handleSortModelChange, handleInitializeSortModel } = useDatagridSorting("roleIdpGroups");
	const { handleToastAlert } = useToastAlert();
	const navigate = useNavigate();
	const { roleId } = useParams();
	const { pathname, state } = useLocation();

	const [roleIdpGroups, setRoleIdPGroups] = useState(dummyRows);
	const [idpGroupsModalOpen, setIdpGroupsModalOpen] = useState(false);
	const [retrievedIdpGroups, setRetrievedIdpGroups] = useState([]);
	const [availableIdpGroups, setAvailableIdpGroups] = useState([]);
	const [idpGroupsToAdd, setIdpGroupsToAdd] = useState([]);
	const [deleteRoleDialogOpen, setDeleteRoleDialogOpen] = useState(false);
	const [editRoleDialogOpen, setEditRoleDialogOpen] = useState(false);
	const [roleNameInput, setRoleNameInput] = useState("");
	const [roleNameError, setRoleNameError] = useState("");
	const [loading, setLoading] = useState(true);
	const [optionsLoading, setOptionsLoading] = useState(true);
	
	const init = () => {
		axios.get(apiRoutes.getRoleIdpGroups(roleId), { headers: authHeader })
			.then((res) => {
				setRoleIdPGroups(res.data);
				setLoading(false);
			})
			.catch((err) => {
				console.log(err);
			});
	};
	
	const removeIdPGroupFromRole = (idpGroupId) => {
		axios.delete(apiRoutes.removeIdpGroupFromRole(roleId, idpGroupId), { headers: authHeader })
			.then(() => {
				init();
				handleToastAlert(messageLevels.INFO, messages.IDP_GROUP_REMOVED_SUCCESS_MSG);
			})
			.catch((err) => {
				console.log(err);
				handleToastAlert(messageLevels.ERROR, messages.IDP_GROUP_REMOVED_ERROR_MSG);
			});
	};

	const columns = useMemo(() => (
		getRoleSSOColumns(
			loading,
			identityProviderName,
			removeIdPGroupFromRole
		)
	), [loading, identityProviderName, removeIdPGroupFromRole]);

	const toggleDeleteRoleDialog = () => {
		setDeleteRoleDialogOpen(!deleteRoleDialogOpen);
	};

	const deleteRole = () => {
		axios.delete(apiRoutes.deleteRole(roleId), {
			headers: authHeader
		}).then(() => {
			updateCoAppNavigation(true, navigationItemReferences.roleNavItemIndex);
			handleToastAlert(messageLevels.INFO, messages.ROLE_DELETION_SUCCESS_MSG(false));
			navigate(pages.roleManagement, { replace: true });
			toggleDeleteRoleDialog();
		}).catch((err) => {
			console.log(err);
			handleToastAlert(messageLevels.ERROR, messages.ROLE_DELETION_ERROR_MSG);
			toggleDeleteRoleDialog();
		});
	};

	const toggleEditRoleDialog = () => {
		setEditRoleDialogOpen(!editRoleDialogOpen);
		setRoleNameInput("");
		setRoleNameError("");
	};

	const handleRoleNameChange = (e) => {
		setRoleNameInput(e.target.value);
		setRoleNameError("");
	};

	const handleRoleNameChangeSubmit = (e) => {
		if (!roleNameInput) return;

		if (state && roleNameInput === state.name) {
			toggleEditRoleDialog();
			return;
		}

		axios.put(apiRoutes.updateRole(roleId), { name: roleNameInput }, { headers: authHeader })
			.then(() => {
				toggleEditRoleDialog();
				updateCoAppNavigation(false, navigationItemReferences.roleNavItemIndex, roleNameInput, pathname);
			})
			.catch((e) => {
				console.log(e);
				if (e.response && e.response.data && e.response.data.message && e.response.data.message.includes("already exists")) {
					setRoleNameError(messages.RESOURCE_NAME_ALREADY_EXISTS_ERROR_MSG("Role"));
					return;
				}
				toggleEditRoleDialog();
				handleToastAlert(messageLevels.ERROR, messages.ROLE_NAME_UPDATE_ERROR_MSG);
			});
	};

	const toggleIdpGroupsModal = () => {
		if (!idpGroupsModalOpen) {
			initIdpGroups();
			setIdpGroupsModalOpen(true);
		} else {
			setIdpGroupsModalOpen(false);
			setIdpGroupsToAdd([]);
		}
	};

	const addIdPGroupToRole = () => {
		if (idpGroupsToAdd.length === 0) return;
		axios.put(apiRoutes.addIdpGroupsToRole(roleId), { idpgroups: idpGroupsToAdd }, { headers: authHeader })
			.then(() => {
				init();
				toggleIdpGroupsModal();
				handleToastAlert(messageLevels.SUCCESS, messages.IDP_GROUP_ADDED_SUCCESS_MSG);
			})
			.catch((error) => {
				console.log(error);
			});
	};

	const initAvailableIdpGroups = (retrievedIdpGroups) => {
		setOptionsLoading(true);
		let available = [];
		if (retrievedIdpGroups && retrievedIdpGroups.length > 0) {
			retrievedIdpGroups.forEach((idpGroup) => {
				if (!roleIdpGroups || !roleIdpGroups.some((roleIdpGroup) => roleIdpGroup.name === idpGroup.externalGroupName)) {
					available.push({ externalGroupId: idpGroup.externalGroupId, name: idpGroup.externalGroupName });
				}
			});
		}
		setAvailableIdpGroups(available);
		setOptionsLoading(false);
	};

	const initIdpGroups = () => {
		axios.get(apiRoutes.getExternalGroups(currentUser.organizationId), { headers: authHeader })
			.then((res) => {
				setRetrievedIdpGroups(res.data);
				initAvailableIdpGroups(res.data);
			})
			.catch((err) => {
				console.log(err);
			});
	};

	const addIdPGroupToIdPGroupsToAdd = (value) => {
		let valueWithIds = [...value].map((groupName) => {
			let foundGroup = availableIdpGroups.find((group) => group.name === groupName);
			return foundGroup;
		});
		setIdpGroupsToAdd(valueWithIds);
	};

	useEffect(() => {
		init();
		handleInitializeSortModel();
	}, [state && state.name]);

	return (
		<>
			<CoAppDatagridHeader
				title={state ? `${state.name} SSO Management` : "Role SSO Management"}
				resourceType="Role"
				subResourceType={identityProviderName + " Groups"}
				addIsPresent={true}
				addOnClickHandler={toggleIdpGroupsModal}
				editIsPresent={state ? !state.isProtected : false}
				editOnClickHandler={toggleEditRoleDialog}
				deleteIsPresent={state ? !state.isProtected : false}
				deleteOnClickHandler={toggleDeleteRoleDialog}
			/>
			<CoAppStandardDataGrid
				columns={columns}
				rows={roleIdpGroups}
				pinnedColumns={["actions", "name"]}
				allowSelection={false}
				targetResource="SSO"
				parentResource="Role"
				parentResourceName={state ? state.name : "Role"}
				loading={loading}
				sortModel={sortModel}
				handleSortModelChange={handleSortModelChange}
			/>
			<CoAppDestructiveConfirmationModal
				dialogOpen={deleteRoleDialogOpen}
				dialogTitle={state ? `Delete ${state.name} role?` : "Delete role?"}
				dialogMessage={state ? messages.DELETION_CONFIRMATION_MSG(state.name, "role") : "Are you sure you want to delete this role?"}
				confirmClickHandler={deleteRole}
				cancelClickHandler={toggleDeleteRoleDialog}
				actionText="Delete"
			/>
			<CoAppEditNameModal
				dialogOpen={editRoleDialogOpen}
				dialogTitle="Edit role name"
				changeHandler={handleRoleNameChange}
				placeholderText={state ? state.name : ""}
				confirmClickHandler={handleRoleNameChangeSubmit}
				cancelClickHandler={toggleEditRoleDialog}
				actionText="Save"
				editNameError={roleNameError}
				setEditNameError={setRoleNameError}
			/>
			{
				identityProviderName && (
					<CoAppAddItemsToListModal
						addButtonTitle="Save"
						inputLabel={identityProviderName + " Group(s)"}
						addItemsIsToggled={idpGroupsModalOpen}
						addItemsToCollection={addIdPGroupToRole}
						addItemsToNewArray={addIdPGroupToIdPGroupsToAdd}
						dialogTitle={`Add ${identityProviderName} group(s) to ${state ? state.name : "role"}`}
						items={availableIdpGroups}
						itemsToAdd={idpGroupsToAdd}
						toggleDialog={toggleIdpGroupsModal}
						loading={optionsLoading}
						valueLabelField="name"
					/>
				)
			}
		</>
	);
}